import { motion } from "framer-motion";
import { Sparkles, Leaf, MapPin, MessageCircle, ShieldCheck, ArrowRight } from "lucide-react";
import { ASSISTANTS } from "@/constants/assistants";
import { useAssistant } from "@/context/AssistantContext";
import usePageTitle from "@/hooks/usePageTitle";

const ICONS = { sparkles: Sparkles, leaf: Leaf, map: MapPin };

export default function Assistants() {
  usePageTitle("AI Assistants");
  const { openWith } = useAssistant();

  return (
    <div>
      <section className="bg-brand-deep">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-sand/20 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-sand/80">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#C9A24B" }} /> Powered by AI · grounded in reviewed content
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-sand">
            Meet your <span style={{ color: "#C9A24B" }}>Ayurveda assistants</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-brand-sand/80">
            Three specialised AI helpers that answer only from Ayurvedam.com's reviewed articles, herbs and
            therapies — with citations and safety notes. They never diagnose, prescribe or promise cures.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
        <div className="grid gap-7 md:grid-cols-3">
          {ASSISTANTS.map((a, i) => {
            const Icon = ICONS[a.icon] || Sparkles;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`assistant-card-${a.id}`}
                className="flex flex-col rounded-2xl border border-brand-line bg-white p-7 shadow-sm"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand text-brand-sand">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-heading text-2xl text-brand-ink">{a.name}</h3>
                <p className="text-sm font-medium text-brand-clay">{a.tagline}</p>
                <p className="mt-3 flex-1 text-brand-muted leading-relaxed">{a.desc}</p>
                <div className="mt-5 space-y-2">
                  {a.suggestions.slice(0, 2).map((s) => (
                    <button
                      key={s}
                      onClick={() => openWith(a.id)}
                      className="block w-full rounded-lg border border-brand-line bg-brand-mist/40 px-3 py-2 text-left text-sm text-brand-ink transition-colors hover:border-brand hover:bg-brand/5"
                    >
                      “{s}”
                    </button>
                  ))}
                </div>
                <button
                  data-testid={`start-${a.id}`}
                  onClick={() => openWith(a.id)}
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 font-medium text-brand-sand transition-transform hover:scale-[1.02]"
                >
                  <MessageCircle className="h-4 w-4" /> Chat with {a.name.split(" ")[0]}
                </button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-brand-line bg-brand-mist/50 p-8 text-center">
          <ShieldCheck className="h-6 w-6 text-brand-sage" />
          <p className="max-w-2xl text-sm text-brand-muted">
            Every assistant is restricted to permitted, reviewed Ayurvedam.com sources. High-risk health topics are
            never auto-answered beyond education, and you'll always be pointed to a qualified practitioner.
          </p>
          <a href="/network" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:gap-2.5 transition-all">
            See how content is shared across the network <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
